import { useState, useEffect, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { getFirebaseAuth } from '../firebase';
import { CreateTicketModal } from './CreateTicketModal';
import type { SupportTicketSummary } from '../api/client';

/**
 * UserProfile — merged profile + help button, pinned bottom-right.
 *
 * Replaces the earlier split between a top-right profile avatar and a
 * bottom-right support/help button. One pill now carries both: menu
 * items for Admin Console, Preferences, Open a ticket, Tickets list,
 * and Sign out. The dropdown opens to the LEFT (and anchored to the
 * BOTTOM) so it clears the map's zoom-cluster stack above the pill.
 *
 * `source` is forwarded to CreateTicketModal so the ticket gets stamped
 * with the originating surface — mirrors what SupportTicketButton used
 * to do.
 */

const fadeInKeyframes = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export interface UserProfileProps {
  /** Zendesk origin surface stamped onto any ticket opened via the
   *  "Open a ticket" menu item. Must match the server-side allowlist in
   *  CreateTicketModal (map | alerts | research | support). */
  source?: 'map' | 'alerts' | 'research' | 'support';
}

export const UserProfile = ({ source = 'map' }: UserProfileProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [canAccessAdmin, setCanAccessAdmin] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketConfirmation, setTicketConfirmation] = useState<SupportTicketSummary | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkAdminPermissions = async () => {
      try {
        const auth = await getFirebaseAuth();
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken(true);
        const res = await fetch('/api/admin/orgs', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (isMounted) {
            const hasOrgs = Array.isArray(data.orgs) && data.orgs.length > 0;
            const isGlobal = data.isGlobalAdmin === true;
            const isRawArray = Array.isArray(data) && data.length > 0;
            if (hasOrgs || isGlobal || isRawArray) {
              setCanAccessAdmin(true);
            }
          }
        }
      } catch (err) {
        console.error("UserProfile: Permission check failed", err);
      }
    };

    checkAdminPermissions();

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      isMounted = false;
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      const auth = await getFirebaseAuth();
      await signOut(auth);
      setIsOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleTicketCreated = (ticket: SupportTicketSummary) => {
    setTicketModalOpen(false);
    setTicketConfirmation(ticket);
    setTimeout(() => setTicketConfirmation(null), 6000);
  };

  // Shared styling for every menu item so there's one source of truth.
  const menuItemStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#5eead4',
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '6px',
    transition: 'background 0.15s',
  };
  const hoverIn  = (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'rgba(0, 128, 128, 0.2)');
  const hoverOut = (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'none');

  return (
    <>
      <style>{fadeInKeyframes}</style>
      <div
        ref={menuRef}
        style={{ position: 'relative' }}
      >
        {/* The Popup Menu — opens UPWARD (bottom: 100%) and to the LEFT
            (right: 100%) of the pill because the pill lives in the
            bottom-right corner above the map's vertical zoom-cluster.
            Opening down/right would either run off-screen or sit under
            the zoom buttons. */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            right: '100%',
            marginBottom: '8px',
            marginRight: '8px',
            background: 'rgba(0, 32, 63, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            padding: '8px',
            minWidth: '200px',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-out',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}>

            {canAccessAdmin && (
              <button
                onClick={() => { setIsOpen(false); navigate('/admin'); }}
                style={menuItemStyle}
                onMouseOver={hoverIn} onMouseOut={hoverOut}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                Admin Console
              </button>
            )}

            {/* Preferences — dispatches the window event AuthGate owns
                the modal mount for. Same pattern across every pill
                instance so we never double-mount the modal. */}
            <button
              onClick={() => {
                setIsOpen(false);
                window.dispatchEvent(new CustomEvent('mosaics:open-preferences'));
              }}
              style={menuItemStyle}
              onMouseOver={hoverIn} onMouseOut={hoverOut}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Preferences
            </button>

            {/* Open a ticket — opens the same modal the retired
                SupportTicketButton used. `source` is stamped so support
                agents can filter by surface of origin. */}
            <button
              onClick={() => { setIsOpen(false); setTicketModalOpen(true); }}
              style={menuItemStyle}
              onMouseOver={hoverIn} onMouseOut={hoverOut}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {/* Question-mark in chat bubble — matches the retired floating help button */}
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-1.5 2-2.5 2.5" />
                <line x1="12" y1="15" x2="12" y2="15.5" />
              </svg>
              Open a ticket
            </button>

            {/* Tickets list — navigates to the full support page. */}
            <button
              onClick={() => { setIsOpen(false); navigate('/support'); }}
              style={menuItemStyle}
              onMouseOver={hoverIn} onMouseOut={hoverOut}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {/* Life-ring / help icon */}
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="4" />
                <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
                <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
                <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
                <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
              </svg>
              My tickets
            </button>

            <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.12)', margin: '4px 0' }} />

            <button
              onClick={handleLogout}
              style={{ ...menuItemStyle, color: '#f87171', fontWeight: 500 }}
              onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          </div>
        )}

        {/* The pill. Same size and visual style as the retired support
            button so moving things around doesn't change the visual
            weight of the bottom-right corner. */}
        <button
          onClick={() => setIsOpen((o) => !o)}
          title="Preferences, tickets and account"
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #006B88, #008080)',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 128, 128, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </button>
      </div>

      {/* Create-ticket modal, owned here so the "Open a ticket" menu
          item has a direct trigger instead of having to proxy through
          another component. */}
      {ticketModalOpen && (
        <CreateTicketModal
          source={source}
          onCancel={() => setTicketModalOpen(false)}
          onCreated={handleTicketCreated}
        />
      )}

      {/* Confirmation toast — same shape and content as the retired
          SupportTicketButton showed. Lingers 6s, then auto-dismisses. */}
      {ticketConfirmation && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            background: '#002F5C',
            border: '1px solid rgba(45, 212, 191, 0.35)',
            borderRadius: 10,
            padding: '12px 14px',
            color: '#e2e8f0',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 999,
            maxWidth: 320,
          }}
        >
          <div style={{ color: '#2dd4bf', flexShrink: 0 }}>
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            Ticket <strong>#{ticketConfirmation.id}</strong> created.
          </div>
          <button
            onClick={() => { setTicketConfirmation(null); navigate('/support'); }}
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6,
              color: '#5eead4',
              padding: '4px 10px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >View</button>
        </div>
      )}
    </>
  );
};
